import { useEffect, useMemo, useState } from 'react';

import dateFormat from 'dateformat';

import { renderInfinityChannels } from '@components/Product/Pricelists/functions';
import { BootstrapTable } from '@components/Table/Table';
import {
  CAPACITY_RATE_TYPES,
  METERED_SERVICES,
  PAYMENT_MODEL,
} from '@config/const';
import { isValidDate } from '@features';

const renderEarliestCancelCell = ({ value }) => {
  if (!isValidDate(value)) {
    return value;
  }

  const date = new Date(value);
  return dateFormat(date, 'defaultDate');
};

export const GeneralTable = ({ data }) => {
  const columns = useMemo(
    () => [
      {
        Header: 'Type of Service',
        accessor: 'type_of_service',
        className: 'col-md-1',
      },
      {
        Header: 'City/Area',
        accessor: 'area',
        className: 'col-md-2',
      },
      {
        Header: 'Type of Number',
        accessor: 'type',
        className: 'col-md-1',
      },
      {
        Header: 'Start Date',
        accessor: 'start_date',
        className: 'col-md-1',
        Cell: ({ value }) => {
          const date = new Date(value);
          return dateFormat(date, 'defaultDate');
        },
      },
      {
        Header: 'Earliest cancel',
        accessor: 'earliest_cancel',
        className: 'col-md-1',
        Cell: renderEarliestCancelCell,
      },
    ],
    []
  );
  return (
    <BootstrapTable
      columns={columns}
      data={data}
      tableProps={() => ({ responsive: true, bordered: true })}
      tableHeader="Number details"
    />
  );
};

export const CapacityPricingNonUsageTable = ({ data }) => {
  const rateType = data.length > 0 ? data[0].rate_type : PAYMENT_MODEL.CAPACITY;

  const meteredColumns = useMemo(
    () => [
      {
        Header: 'DID',
        className: 'text-center',
        columns: [
          {
            Header: 'NRC',
            accessor: 'nrc_price',
            className: 'col-md-1',
          },
          {
            Header: 'MRC',
            accessor: 'mrc_price',
            className: 'col-md-1',
          },
        ],
      },
      {
        Header: 'Dedicated channels',
        className: 'text-center',
        columns: [
          {
            Header: 'Default channels',
            accessor: 'default_channels',
            className: 'col-md-1',
            Cell: renderInfinityChannels,
          },
          {
            Header: 'Max channels',
            accessor: 'max_channels',
            className: 'col-md-2',
            Cell: renderInfinityChannels,
          },
          {
            Header: 'NRC extra channel',
            accessor: 'nrc_extra_channel_price',
            className: 'col-md-1',
          },
          {
            Header: 'MRC extra channel',
            accessor: 'mrc_extra_channel_price',
            className: 'col-md-1',
          },
        ],
      },
      {
        Header: 'Port out fee',
        accessor: 'port_out_fee_price',
        className: 'col-md-1',
      },
    ],
    []
  );
  const capacityColumns = useMemo(
    () => [
      {
        Header: 'DID with 2 channels',
        className: 'text-center',
        columns: [
          {
            Header: 'NRC',
            accessor: 'nrc_price',
            className: 'col-md-1',
          },
          {
            Header: 'MRC',
            accessor: 'mrc_price',
            className: 'col-md-1',
          },
        ],
      },
      {
        Header: 'Shared channels',
        className: 'text-center',
        columns: [
          {
            Header: 'NRC',
            accessor: 'shared_channel_nrc',
            className: 'col-md-1',
          },
          {
            Header: 'MRC 1-10',
            accessor: 'shared_channel_mrc_lt_10',
            className: 'col-md-1',
          },
          {
            Header: 'MRC 11-100',
            accessor: 'shared_channel_mrc_10_to_100',
            className: 'col-md-1',
          },
          {
            Header: 'MRC > 100',
            accessor: 'shared_channel_mrc_gt_100',
            className: 'col-md-1',
          },
          {
            Header: 'Max',
            accessor: 'max_shared_channels',
            className: 'col-md-1',
          },
        ],
      },
    ],
    []
  );
  const columns = {
    [CAPACITY_RATE_TYPES.CAPACITY]: capacityColumns,
    [CAPACITY_RATE_TYPES.METERED]: meteredColumns,
    [CAPACITY_RATE_TYPES.HYBRID]: meteredColumns,
  };

  return Object.keys(data).length > 0 ? (
    <BootstrapTable
      columns={columns[rateType]}
      data={data}
      tableProps={() => ({ responsive: true, bordered: true })}
      tableHeader={`Rate Type: ${rateType}`}
    />
  ) : null;
};

export const MeteredPricingNonUsageTable = ({ data }) => {
  const columns = useMemo(
    () => [
      {
        Header: 'NRC',
        accessor: 'nrc_price',
        className: 'col-md-1',
      },
      {
        Header: 'MRC',
        accessor: 'mrc_price',
        className: 'col-md-1',
      },
      {
        Header: 'Default channels',
        accessor: 'default_channels',
        className: 'col-md-1',
        Cell: renderInfinityChannels,
      },
      {
        Header: 'max Channels',
        accessor: 'max_channels',
        className: 'col-md-2',
        Cell: renderInfinityChannels,
      },
      {
        Header: 'NRC extra channel',
        accessor: 'nrc_extra_channel_price',
        className: 'col-md-1',
      },
      {
        Header: 'MRC extra channel',
        accessor: 'mrc_extra_channel_price',
        className: 'col-md-1',
      },
      {
        Header: 'Port out fee',
        accessor: 'port_out_fee_price',
        className: 'col-md-1',
      },
    ],
    []
  );
  return Object.keys(data).length > 0 ? (
    <BootstrapTable
      columns={columns}
      data={data}
      tableProps={() => ({ responsive: true, bordered: true })}
      tableHeader="Pricing Non-usage"
    />
  ) : null;
};

export class PaymentModelPricingNonUsageTablesFactory {
  constructor(paymentModel, didType) {
    this.paymentModel = paymentModel;
    this.didType = didType;

    this.PAYMENT_MODEL = PAYMENT_MODEL;
    this.METERED_SERVICES = METERED_SERVICES;
  }

  getTableClass() {
    const data = {
      [this.PAYMENT_MODEL.METERED]: MeteredPricingNonUsageTable,
      [this.PAYMENT_MODEL.CAPACITY]: CapacityPricingNonUsageTable,
    };
    if (
      this.paymentModel === this.PAYMENT_MODEL.CAPACITY &&
      Object.values(this.METERED_SERVICES).includes(this.didType)
    ) {
      return data[this.PAYMENT_MODEL.METERED];
    }
    return data[this.paymentModel];
  }
}

export const PricingUsageTable = ({ data }) => {
  const [hiddenPriceMinColumns, setHiddenPriceMinColumns] = useState([]);
  useEffect(() => {
    if (data.length > 0) {
      const columns = Object.entries(data[0])
        .filter(([key, value]) => value === null && key.includes('minute_'))
        .map((key) => key[0]);

      setHiddenPriceMinColumns(columns);
    }
  }, [data]);
  const columns = useMemo(
    () => [
      {
        Header: 'Call set-up',
        accessor: 'call_setup_price',
        className: 'col-md-1',
      },
      {
        Header: 'Price per min Fix',
        accessor: 'minute_fixed_price',
        className: 'col-md-1',
      },
      {
        Header: 'Price per min Mob',
        accessor: 'minute_mobile_price',
        className: 'col-md-1',
      },

      {
        Header: 'Price per min Pay Phone',
        accessor: 'minute_payphone_price',
        className: 'col-md-1',
      },
      {
        Header: 'Price per min Shadow',
        accessor: 'minute_shadow_routing_price',
        className: 'col-md-1',
      },
      {
        Header: 'Price per min Fixed Rodasy',
        accessor: 'minute_fixed_rodasy_price',
        className: 'col-md-1',
      },
      {
        Header: 'Price per min Mobile Rodasy',
        accessor: 'minute_mobile_rodasy_price',
        className: 'col-md-1',
      },
      {
        Header: 'Price per min International',
        accessor: 'minute_international_price',
        className: 'col-md-1',
      },
      {
        Header: 'Price per min Unknown',
        accessor: 'minute_unknown_price',
        className: 'col-md-1',
      },
      {
        Header: 'Billing Increment',
        accessor: 'billing_increment',
        className: 'col-md-2',
      },
    ],
    [hiddenPriceMinColumns]
  );
  return Object.keys(data).length > 0 ? (
    <BootstrapTable
      columns={columns}
      hiddenColumns={hiddenPriceMinColumns}
      data={data}
      tableProps={() => ({ responsive: true, bordered: true })}
      tableHeader="Pricing inbound"
    />
  ) : null;
};
